import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  IActivo,
  ICreateEnvioVehiculo,
  ICreateTratamientoEvento,
  IEnvioVehiculo,
  IFilter,
  IGeoJSONPoint,
  IPopulate,
  IQueryParam,
  IReporteBase,
  IReporteGenerico,
} from 'modelos/src';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { TratamientoEventosService } from '../../../../../auxiliares/servicios/http/tratamientos-eventos.service';
import { Subscription } from 'rxjs';
import { EnviosVehiculosService } from '../../../../../auxiliares/servicios/http/envios-vehiculos';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import {
  MapCardConfig,
  MapCardComponent,
} from '../../../../../componentes/map-card/map-card.component';
import {
  MarkerMapa,
  MapaFacilData,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { VehiculosLayerManager } from '../../../../../auxiliares/mapbox/entity-managers/vehiculos-layer.manager';

export interface IVehiculoEnvio extends IActivo {
  cantidadEnvios?: number;
  distanciaAlEvento?: number;
  ultimoReporte?: IReporteBase<'Tracker 4G'>;
}

@Component({
  selector: 'app-enviar-movil',
  templateUrl: './enviar-movil.component.html',
  styleUrl: './enviar-movil.component.scss',
  standalone: false,
})
export class EnviarMovilComponent implements OnInit, OnDestroy {
  data = inject<{
    idsEventos: string[];
    position?: IGeoJSONPoint;
  }>(MAT_DIALOG_DATA);
  helper = inject(HelperService);
  private service = inject(TratamientoEventosService);
  private envioVehiculoService = inject(EnviosVehiculosService);
  dialogRef = inject<MatDialogRef<EnviarMovilComponent>>(MatDialogRef);
  private lista2 = inject(Lista2Service);
  private vehiculosLayerManager = inject(VehiculosLayerManager);

  @ViewChild(MapCardComponent) mapCardComponent?: MapCardComponent;

  public loading: boolean = true;
  public mapCardConfig?: MapCardConfig;
  public formTratamiento?: FormGroup;
  public formEnvio?: FormGroup;
  //listar
  public vehiculos?: IVehiculoEnvio[];
  public vehiculos$?: Subscription;
  public enviosVehiculos$?: Subscription;
  public reportes$?: Subscription;

  private createForm() {
    this.formTratamiento = new FormGroup({
      idsEventos: new FormControl(this.data?.idsEventos),
      nota: new FormControl(),
      notaInterna: new FormControl(),
      estado: new FormControl('En Atención'),
    });
    this.formEnvio = new FormGroup({
      idsEventos: new FormControl(this.data?.idsEventos),
      detalles: new FormControl(),
      idConductor: new FormControl(),
      idActivo: new FormControl(),
      estado: new FormControl('Asignado'),
    });
  }

  public async listarVehiculos() {
    // 'vehiculo.idChofer': { $exists: true, $ne: null },
    const filter: IFilter<IActivo> = {
      funcion: 'Seguridad Privada',
    };
    const populate: IPopulate[] = [
      {
        path: 'vehiculo.chofer',
        select: 'datosPersonales.nombre usuario',
      },
    ];

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
    };
    this.vehiculos$?.unsubscribe();
    this.vehiculos$ = this.lista2.vehiculos(query).subscribe(async (data) => {
      await Promise.all([
        this.listarUltimoReporteVehiculos(data.datos),
        this.listarEnviosVehiculo(data.datos),
      ]);
    });
  }

  public async listarUltimoReporteVehiculos(vehiculos: IVehiculoEnvio[]) {
    const idsActivos = vehiculos.map((a) => a._id);

    const populate: IPopulate[] = [
      {
        path: 'activo',
      },
    ];
    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: { $in: idsActivos },
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
    };
    //
    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      .ultimosReportesGenericos<'Tracker 4G'>(query)
      .subscribe((data) => {
        vehiculos.forEach((v) => {
          const reporte = data.datos.find((r) =>
            r.idsAsignados.includes(v._id),
          );
          v.ultimoReporte = reporte;
          if (!reporte) return;
          const valores = this.helper.getValores(reporte);
          const position = valores.geojson;
          if (!position || !position.coordinates) return;

          //Ubicación del vehículo (último reporte)
          const coord1: [number, number] = [
            position.coordinates[0],
            position.coordinates[1],
          ];

          //Ubicación del evento
          const coord2: [number, number] = [
            this.data.position?.coordinates?.[0] ?? 0,
            this.data.position?.coordinates?.[1] ?? 0,
          ];

          if (coord1[0] && coord2[0]) {
            const distanciaAlEvento = this.helper.distanciaEntrePuntos(
              coord1,
              coord2,
            );
            v.distanciaAlEvento = distanciaAlEvento;
          }
        });
        this.vehiculos = vehiculos.filter((x) => x.ultimoReporte);
        this.vehiculos = this.vehiculos.sort((a, b) => {
          return a.distanciaAlEvento - b.distanciaAlEvento;
        });
        this.generarMapCard();
      });
  }

  public async listarEnviosVehiculo(vehiculos: IVehiculoEnvio[]) {
    const idsActivos = vehiculos.map((a) => a._id);
    const filter: IFilter<IEnvioVehiculo> = {
      estado: { $nin: ['Finalizado', 'Rechazado'] },
      idActivo: { $in: idsActivos },
    };

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
    };
    this.enviosVehiculos$?.unsubscribe();
    this.enviosVehiculos$ = this.lista2
      .envioVehiculos(query, 30)
      .subscribe((data) => {
        vehiculos.forEach((v) => {
          const envios = data.datos.filter((e) => e.idActivo === v._id);
          v.cantidadEnvios = envios.length;
        });
      });
  }

  //Buscar
  public buscarVehiculo = (term: string, item: IActivo) => {
    if (item.identificacion?.toLowerCase().includes(term.toLowerCase()))
      return true;
    if (
      item.vehiculo?.chofer?.datosPersonales?.nombre
        ?.toLowerCase()
        .includes(term.toLowerCase())
    )
      return true;
    if (
      item.vehiculo?.chofer?.usuario?.toLowerCase().includes(term.toLowerCase())
    )
      return true;
    return false;
  };

  public asignarVehiculoConductor(data: IVehiculoEnvio) {
    this.formEnvio.patchValue({ idActivo: data._id });
    this.formEnvio.patchValue({ idConductor: data?.vehiculo?.chofer?._id });

    // Mostrar solo el nombre del vehículo seleccionado
    this.vehiculosLayerManager.mostrarNombreDeVehiculo(data._id);

    // Hacer zoom al vehículo seleccionado si es IVehiculoEnvio
    if (data.ultimoReporte) {
      this.centrarMapaEnVehiculo(data);
    }
  }

  private centrarMapaEnVehiculo(vehiculo: IVehiculoEnvio) {
    if (!vehiculo.ultimoReporte) return;

    const valores = this.helper.getValores(vehiculo.ultimoReporte);
    const geojson = valores?.geojson;

    if (geojson?.coordinates) {
      const [lng, lat] = geojson.coordinates;

      // Acceder al mapa a través del MapCardComponent
      const map = this.mapCardComponent?.getMap();
      if (map) {
        map.flyTo({
          center: [lng, lat],
          zoom: 16,
          duration: 5000,
        });
      }
    }
  }

  public onMarkerClick(entity: any) {
    if (!entity) {
      this.vehiculosLayerManager.mostrarNombreDeVehiculo(null);
    }
    // El entity puede venir en diferentes formatos dependiendo del layer
    let idReporteVehiculo: string | undefined;
    // Intentar obtener el ID de diferentes propiedades posibles
    if (entity?.id) {
      idReporteVehiculo = entity.id; //No llega la id del vehículo, llega la id del último reporte
    } else if (entity?.properties?.id) {
      idReporteVehiculo = entity.properties.id;
    }

    if (idReporteVehiculo) {
      const vehiculo = this.vehiculos?.find(
        (v) => v?.ultimoReporte?._id === idReporteVehiculo,
      );
      if (vehiculo) {
        this.asignarVehiculoConductor(vehiculo);
      }
    }
  }

  // Mapa

  private generarMapCard(): void {
    const markers: MarkerMapa[] = [];

    // Agregar markers de los móviles
    if (this.vehiculos) {
      for (const vehiculo of this.vehiculos) {
        if (!vehiculo.ultimoReporte) continue;
        const valores = this.helper.getValores(vehiculo.ultimoReporte);
        const geojson = valores.geojson;
        if (!geojson?.coordinates) continue;

        const marker: MarkerMapa = {
          label: 'vehiculo',
          coor: geojson.coordinates as [number, number],
          text: vehiculo.identificacion,
          id: vehiculo._id,
          data: vehiculo.ultimoReporte, //Al mapa fácil se le envía el reporte de los vehículos
        };
        markers.push(marker);
      }
    }

    // Agregar marker del evento
    if (this.data.position?.coordinates) {
      const markerEvento: MarkerMapa = {
        label: 'alarma',
        coor: this.data.position.coordinates as [number, number],
        text: 'Evento',
        centrar: true,
      };
      markers.push(markerEvento);
    }

    // Calcular el centro del mapa (usar ubicación del evento o primer vehículo)
    let center: [number, number] = [0, 0];
    if (this.data.position?.coordinates) {
      center = this.data.position.coordinates as [number, number];
    } else if (markers.length > 0 && markers[0].coor) {
      center = markers[0].coor;
    }

    const mapData: MapaFacilData = {
      markers,
      center,
      zoom: 14,
      selectorBaseLayer: true,
      clustering: {
        enabled: false, // Desactivar clustering para mostrar todos los markers individuales con su texto
      },
    };

    this.mapCardConfig = {
      title: 'Ubicación de Móviles y Evento',
      icon: 'map',
      color: 'blue',
      mapOptions: mapData,
      nombreMap: 'MapEnviarMovil',
      disablePopups: true, // Desactivar popups
    };
  }

  //
  public async onSubmit() {
    this.loading = true;
    try {
      const dataTratamiento = this.getData();
      dataTratamiento.nota = dataTratamiento.nota
        ? `Envío de Móvil - ${dataTratamiento.nota}`
        : 'Envío de Móvil';
      const dataEnvio = this.getDataEnvio();

      await Promise.all([
        this.envioVehiculoService.create(dataEnvio),
        this.service.createGenerico(dataTratamiento),
      ]);
      this.helper.notifSuccess('Creado correctamente');
      this.dialogRef.close(dataTratamiento);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  public getData(): ICreateTratamientoEvento {
    const data: ICreateTratamientoEvento = this.formTratamiento?.value;
    return data;
  }
  public getDataEnvio(): ICreateEnvioVehiculo {
    const data: ICreateEnvioVehiculo = this.formEnvio?.value;
    return data;
  }

  async ngOnInit() {
    this.createForm();
    await this.listarVehiculos();
    this.loading = false;
  }

  ngOnDestroy() {
    this.enviosVehiculos$?.unsubscribe();
    this.vehiculos$.unsubscribe();
    this.reportes$?.unsubscribe();

    // Limpiar selección de nombre de vehículo
    this.vehiculosLayerManager.mostrarNombreDeVehiculo(null);
  }
}
